import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, MessageSquareText } from "lucide-react";
import { API } from "../lib/api";
import { waLink } from "../lib/whatsapp";

const WELCOME = "Welcome to Easyilaaz. I am your concierge assistant. May I know which country you're reaching us from, and the specialty you'd like guidance on?";

export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("ez_chat_session") || null);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });

      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let carry = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        carry += decoder.decode(value, { stream: true });
        const events = carry.split("\n\n");
        carry = events.pop() || "";
        for (const evt of events) {
          const lines = evt.split("\n");
          let evType = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event:")) evType = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
          if (evType === "session") {
            setSessionId(data);
            localStorage.setItem("ez_chat_session", data);
          } else if (evType === "done") {
            // finished
          } else if (evType === "error") {
            setMessages((m) => {
              const copy = m.slice();
              copy[copy.length - 1] = { role: "assistant", content: "Apologies, our concierge line is momentarily unavailable. Please try again or use the Contact form." };
              return copy;
            });
          } else {
            const chunk = data.replace(/\\n/g, "\n");
            setMessages((m) => {
              const copy = m.slice();
              copy[copy.length - 1] = { role: "assistant", content: (copy[copy.length - 1].content || "") + chunk };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = m.slice();
        copy[copy.length - 1] = { role: "assistant", content: "Apologies — our concierge line is momentarily unavailable. Please try again shortly." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        className="fixed z-40 bottom-6 right-6 lg:bottom-8 lg:right-8 h-14 w-14 rounded-full bg-[#0B1F3A] text-white shadow-[0_10px_40px_rgba(11,31,58,0.35)] hover:bg-[#C8A96A] transition-colors duration-300 flex items-center justify-center border border-[#C8A96A]/40"
        aria-label="Open concierge chat"
      >
        {open ? <X strokeWidth={1.25} /> : <MessageCircle strokeWidth={1.25} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-40 bottom-24 right-4 lg:right-8 w-[calc(100vw-2rem)] max-w-[400px] h-[560px] bg-white border border-[#E6E9EF] rounded-sm shadow-[0_30px_80px_rgba(11,31,58,0.18)] flex flex-col overflow-hidden"
        >
          <div className="relative bg-[#0B1F3A] text-white px-5 py-4">
            <div className="absolute inset-x-0 bottom-0 gold-rule opacity-70" />
            <div className="overline text-[#C8A96A]">Private</div>
            <div className="font-serif text-xl mt-1">Concierge Assistant</div>
            <div className="text-[11px] text-white/60 mt-1">Not medical advice. For guidance and to arrange a private consultation.</div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-[#F5F7FA]/50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}-${i}`}
                  className={`max-w-[85%] px-4 py-3 text-[14px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0B1F3A] text-white rounded-t-2xl rounded-bl-2xl"
                      : "bg-white text-[#0B1F3A] border border-[#E6E9EF] rounded-t-2xl rounded-br-2xl"
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#E6E9EF] p-3 flex items-center gap-2 bg-white">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Type your question…"
              className="flex-1 h-11 px-4 text-[14px] bg-[#F5F7FA] border border-transparent focus:border-[#C8A96A] focus:outline-none rounded-full transition-colors duration-200"
              disabled={busy}
            />
            <button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={busy || !input.trim()}
              className="h-11 w-11 rounded-full bg-[#0B1F3A] text-white hover:bg-[#C8A96A] disabled:opacity-40 flex items-center justify-center transition-colors duration-200"
              aria-label="Send"
            >
              <Send size={16} strokeWidth={1.5} />
            </button>
          </div>
          <a
            href={waLink("Hello Easyilaaz Concierge — I would like to continue speaking with a human on WhatsApp.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="chat-whatsapp-handoff"
            className="border-t border-[#E6E9EF] bg-[#F5F7FA] px-5 py-3 text-[11px] tracking-[0.16em] uppercase text-[#0B1F3A]/70 hover:text-[#C8A96A] hover:bg-white flex items-center justify-center gap-2 transition-colors duration-200"
          >
            <MessageSquareText size={13} strokeWidth={1.25} /> Prefer a human? Continue on WhatsApp
          </a>
        </div>
      )}
    </>
  );
}
